import { Alert } from '@components/alert';
import { createForm, useCreateFormStore } from '@components/form';
import { Modal, hideModal, useModal } from '@components/modal';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { slugify } from '@utils/strings';

import { AttributeFieldType } from 'api/attributes/requests';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

import { Property } from './shared/output-list';

type Data = {
  name: string;
  type: 'text' | 'paragraph' | 'date' | 'number' | 'decimal' | 'single_select' | 'boolean';
  options?: { value: string }[];
  instructions: string;
};

const Form = createForm<Data>();

export const useOutputModal = () => useModal(OutputModal);

type OutputModalProps = {
  setValue: (newValue: Array<Property>) => void;
  getValue: () => Array<Property>;
  currentIndex?: number;
};

const OutputModal = ({ setValue, getValue, currentIndex = -1 }: OutputModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const type = formStore.useFieldValue('type');
  const currentItem = currentIndex > -1 ? getValue()[currentIndex] : undefined;
  const optionTypes: AttributeFieldType[] = [
    'text',
    'paragraph',
    'number',
    'decimal',
    'date',
    'single_select',
    'boolean',
  ];

  const options: SelectOption[] = optionTypes.map((type) => ({
    label: (
      <Stack orientation="horizontal" space={2}>
        <AttributeIcon type={type} />
        <Text text={t(type)} />
      </Stack>
    ),
    value: type,
  }));

  const onSubmit = (values: Data) => {
    const currentOutput = getValue() ?? ([] as Property[]);

    const updatedValues = {
      ...values,
      label: values.name,
      name: currentItem?.name ?? slugify(values.name), // Preserve existing name when editing
    };

    const updatedOutput =
      currentIndex > -1
        ? currentOutput.map((item, index) => (index === currentIndex ? updatedValues : item))
        : [...currentOutput, updatedValues];

    setValue(updatedOutput);
    hideModal();
  };

  return (
    <Modal>
      <Form onSubmit={onSubmit} store={formStore}>
        <Modal.Content>
          <Stack space={4}>
            <Form.TextField
              id="name"
              label={t('name')}
              validators={['required']}
              defaultValue={currentItem?.label}
            />
            <Form.Select
              options={options}
              id="type"
              label={t('type')}
              validators={['required']}
              defaultValue={currentItem?.type}
            />
            {type === 'single_select' && (
              <Form.TokenListField
                id="options"
                label={t('tokenOptions')}
                defaultValue={currentItem?.options}
              />
            )}
            <Alert variant="info">
              <Text text={t('instructionsHelp')} />
            </Alert>
            <Form.Textarea
              id="instructions"
              label={t('instructions')}
              validators={['required']}
              defaultValue={currentItem?.instructions}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} onClick={hideModal} />
            <Form.Submit text={t('core.save')} />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    name: 'Output name',
    namePlaceholder: 'Enter a name',
    type: 'Type',
    text: 'Short text',
    paragraph: 'Paragraph',
    date: 'Date',
    multi_select: 'Multi select',
    single_select: 'Token',
    tokenOptions: 'Token options',
    phone_number: 'Phone number',
    email: 'Email',
    file: 'File',
    file_multi: 'File (Multi)',
    number: 'Number (Integer)',
    decimal: 'Decimal (Float)',
    boolean: 'Boolean',
    user: 'User',
    instructionsHelp: 'Give the model instructions on how to generate this output',
    instructions: 'Instructions',
    instructionsPlaceholder: 'Enter instructions',
  },
};
